import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { buildUrl, toastServerError } from "helpers";
import { ApiError, ApiServiceError, instance } from "libs/api";

import { AiAssistantExpertSettings } from "../../aiAgents.types";
import {
  AI_ASSISTANTS_QUERY_KEY,
  AiAssistantSubRoute,
  ApiRoutesAiAssistants
} from "../../constants/enums";

const key = (assistantId: string) => [
  AI_ASSISTANTS_QUERY_KEY,
  "expert-settings",
  assistantId
];

const expertBase = (assistantId: string) =>
  buildUrl(
    ApiRoutesAiAssistants.Assistants,
    assistantId,
    AiAssistantSubRoute.ExpertSettings
  );

export const useGetExpertSettings = (
  assistantId: string | undefined,
  enabled: boolean
) => {
  const query = useQuery<
    AiAssistantExpertSettings,
    ApiServiceError<ApiError>
  >({
    queryKey: key(assistantId ?? ""),
    queryFn: async () => await instance.get(expertBase(assistantId!)),
    enabled: Boolean(assistantId) && enabled,
    onError: toastServerError
  });

  return { expertSettings: query.data ?? null, ...query };
};

export const useUpdateExpertSettings = (assistantId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    AiAssistantExpertSettings,
    ApiServiceError<ApiError>,
    { systemPrompt: string }
  >({
    mutationFn: async (body) => await instance.patch(expertBase(assistantId), body),
    onError: toastServerError,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: key(assistantId) })
  });
};
